<script setup lang="ts">
import { FolderKanban, LayoutDashboard, LogOut, Send, Settings, Users, UsersRound } from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import { Button } from '@/components/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import LogoMark from '@/components/public/LogoMark.vue'
import { useAuthStore } from '@/stores/authStore'

const route = useRoute()
const authStore = useAuthStore()

const handleLogout = async () => {
  await authStore.logout()
}
</script>

<template>
  <Sidebar>
    <SidebarHeader class="flex h-14 justify-center border-b px-4">
      <router-link to="/dashboard" class="flex items-center" aria-label="Kantiro home">
        <LogoMark class="h-8 w-auto dark:hidden" />
        <LogoMark variant="light" class="hidden h-8 w-auto dark:block" />
      </router-link>
    </SidebarHeader>

    <SidebarContent>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu class="gap-y-1 [&_[data-active=true]]:font-semibold">
            <SidebarMenuItem>
              <SidebarMenuButton as-child :is-active="route.path === '/dashboard'">
                <router-link to="/dashboard">
                  <LayoutDashboard />
                  <span>Dashboard</span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton as-child :is-active="route.path.startsWith('/clients')">
                <router-link to="/clients">
                  <Users />
                  <span>Clients</span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton as-child :is-active="route.path.startsWith('/campaigns')">
                <router-link to="/campaigns">
                  <FolderKanban />
                  <span>Campaigns</span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton
                as-child
                :is-active="route.path.startsWith('/posts')"
              >
                <router-link to="/posts">
                  <Send />
                  <span>Posts</span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton as-child :is-active="route.path.startsWith('/team')">
                <router-link to="/team">
                  <UsersRound />
                  <span>Team</span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton
                as-child
                :is-active="route.path.startsWith('/settings') || route.path.startsWith('/platform-connections')"
              >
                <router-link to="/settings">
                  <Settings />
                  <span>Settings</span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>

          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>

    <SidebarFooter class="border-t">
      <div class="flex items-center justify-between gap-2 px-2 py-2">
        <router-link to="/profile" class="min-w-0 rounded-md hover:bg-sidebar-accent">
          <p class="truncate text-sm font-medium">
            {{ authStore.user?.email ?? 'User' }}
          </p>
          <p class="truncate text-xs text-muted-foreground">
            {{ authStore.organization?.name || 'Your organisation' }}
          </p>
        </router-link>
        <Button variant="ghost" size="icon-sm" type="button" title="Sign out" @click="handleLogout">
          <LogOut class="size-4" />
        </Button>
      </div>
    </SidebarFooter>
  </Sidebar>
</template>
